import { createLastBarMotion } from "./motion/lastBarMotion";
import { createScrollMotion } from "./motion/scrollMotion";
import { createAutoscaleMotion } from "./motion/autoscaleMotion";
import { createViewFollow } from "./motion/viewFollow";
import { watchMotionPreference } from "./motion/motionPreference";
import { isViewFollowingBars } from "./scales/newBarScroll";

const now = () => performance.now();

const readView = (timeScale) => ({
  barSpacing: timeScale.barSpacing,
  rightOffset: timeScale.rightOffset,
});

const isSameView = (first, second) =>
  first.barSpacing === second.barSpacing &&
  first.rightOffset === second.rightOffset;

/**
 * Smooth movement of the chart (E5): a tick moves the last bar, the last
 * price line, its badge and the Line dot; a new bar grows out of its open
 * and lands in the room the followed view has opened: while autoscroll is
 * on and the last bar is visible, the view scrolls evenly by one bar per
 * bar interval (viewFollow), what it missed slides; autoscale settles when
 * other bars come into view. Frames run only while something moves or the
 * view follows (scheduler.animate). Gestures apply at once: a view
 * changed between frames by anything but the animator stops the slide and
 * snaps autoscale; following stops only when the last bar leaves the
 * screen. No animation with reduced motion or in a hidden tab: the view
 * stands still and slides by one bar with a new bar, at once.
 *
 * @param {object} engine - ChartEngine: scheduler, timeScale, invalidate(),
 *   shownBars (the series and the indicators, createLayers), data
 *   (DataController), toggles
 */
export const createAnimator = (engine) => {
  const { scheduler, timeScale, shownBars } = engine;
  const lastBar = createLastBarMotion();
  const scroll = createScrollMotion(timeScale);
  const autoscale = createAutoscaleMotion();
  const follow = createViewFollow(timeScale);
  let isSnapPending = true;
  let isFrameSnapped = true;
  let frameTime = now();
  let viewAfterFrame = null;
  let stopFrames = null;

  const isActive = (time) =>
    follow.isFollowing ||
    lastBar.isActive(time) ||
    scroll.isActive(time) ||
    autoscale.isActive(time);

  const keepFramesComing = () => {
    if (stopFrames || !isActive(now())) return;

    stopFrames = scheduler.animate(() => {
      const isStillActive = isActive(now());

      if (!isStillActive) stopFrames = null;

      return isStillActive;
    });
  };

  // The animator moved the view itself, so it is not taken for a gesture
  const recordView = () => {
    viewAfterFrame = readView(timeScale);
  };

  const finish = () => {
    if (lastBar.finish()) shownBars.updateLastBar(lastBar.bars);

    scroll.finish();
    follow.freeze();
    recordView();
    isSnapPending = true;
  };

  const preference = watchMotionPreference(() => {
    finish();
    engine.invalidate();
  });

  const motion = () => ({ isAnimated: preference.isEnabled, now: now() });

  // Time of the last bar for the follow phase: wall clock, as the bar times
  const barTime = () => {
    const { store, barDurationMs } = engine.data;

    return { lastBarTime: store.bars[store.lastIndex]?.time, barDurationMs, now: Date.now() };
  };

  const followTime = () => {
    const { store, barDurationMs } = engine.data;
    const shouldFollow =
      preference.isEnabled &&
      store.bars.length > 0 &&
      barDurationMs > 0 &&
      isViewFollowingBars(timeScale, engine.toggles.autoscrollEnabled);

    // A slide in progress ends first, so freezing keeps the bars where they are
    if (follow.isFollowing && !shouldFollow) scroll.finish();

    follow.update({ shouldFollow, ...barTime() });
  };

  return {
    /** The view scrolls with time (viewFollow): a new bar follows it. */
    get isFollowingView() {
      return follow.isFollowing;
    },

    /** Animations are on: no reduced motion, the tab is visible. */
    get isMotionEnabled() {
      return preference.isEnabled;
    },

    /** @param {object[]} storeBars - a tick changed the last one */
    updateLastBar: (storeBars) => {
      lastBar.updateLastBar(storeBars, motion());
      shownBars.updateLastBar(lastBar.bars);
      keepFramesComing();
    },

    /**
     * A bar was added on the right (the store has it already).
     *
     * @param {boolean} isFollowing - the view follows it: lands in the opened
     *   room, slides by what is left (one bar when the view stood still)
     */
    appendBar: (isFollowing) => {
      const jump = isFollowing && follow.isFollowing ? follow.appendBar(barTime()) : 1;

      scroll.appendBar({ ...motion(), isFollowing, jump });
      recordView();
      keepFramesComing();
    },

    /**
     * Gives the series and the indicators all bars; a new last bar grows out of its open.
     *
     * @param {object[]} storeBars
     * @param {boolean} isNewBar
     */
    setBars: (storeBars, isNewBar) => {
      shownBars.setBars(lastBar.setBars(storeBars, { ...motion(), isNewBar }), isNewBar);
      keepFramesComing();
    },

    /** New history: the next frame shows everything at its place at once. */
    snap: finish,

    /**
     * Moves the view smoothly (at once without animation); a gesture stops it.
     *
     * @param {{ barSpacing: number, rightOffset: number }} view - px; bars between
     *   the last bar and the price axis
     */
    moveViewTo: (view) => {
      scroll.moveTo(view, motion());
      recordView();
      keepFramesComing();
      engine.invalidate();
    },

    /** Before the layout of a frame: moves the last bar and the view. */
    beginFrame: () => {
      const isChangedOutside =
        viewAfterFrame && !isSameView(viewAfterFrame, readView(timeScale));

      if (isChangedOutside) scroll.cancel();

      frameTime = now();
      isFrameSnapped = isSnapPending || isChangedOutside || !preference.isEnabled;
      isSnapPending = false;

      if (lastBar.step(frameTime)) shownBars.updateLastBar(lastBar.bars);

      followTime();
      scroll.step(frameTime);
    },

    /**
     * Range the autoscale fits in this frame (layoutFrame).
     *
     * @param {{ min: number, max: number } | null} targetRange - prices of the visible bars
     * @param {{ first: number, last: number }} visibleBars
     * @returns {{ min: number, max: number } | null}
     */
    smoothPriceRange: (targetRange, visibleBars) =>
      autoscale.smooth(targetRange, visibleBars, { isSnapped: isFrameSnapped, now: frameTime }),

    /** After a frame: remembers the view and keeps frames coming while something moves. */
    endFrame: () => {
      recordView();
      keepFramesComing();
    },

    destroy: () => {
      preference.stop();
      stopFrames?.();
    },
  };
};
